import { useState, useMemo } from "react";
import { Search, Sparkles } from "lucide-react";
import {
  getAllPrompts,
  PROMPT_CATEGORY_LABELS,
  WORKFLOWS,
  type PromptDefinition,
  type PromptCategory,
} from "@/data/prompts";
import PromptDetailModal from "./PromptDetailModal";

export default function PromptsList() {
  const [search, setSearch] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState<PromptDefinition | null>(null);

  const allPrompts = useMemo(() => getAllPrompts(), []);

  const filtered = useMemo(() => {
    if (!search.trim()) return allPrompts;
    const q = search.toLowerCase();
    return allPrompts.filter(
      p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.promptText.toLowerCase().includes(q)
    );
  }, [allPrompts, search]);

  // Group by category
  const grouped = useMemo(() => {
    const groups: Record<string, PromptDefinition[]> = {};
    for (const p of filtered) {
      if (!groups[p.category]) groups[p.category] = [];
      groups[p.category].push(p);
    }
    return groups;
  }, [filtered]);

  const categoryOrder: PromptCategory[] = [
    'evaluation_system',
    'evaluation_criteria',
    'evaluation_output',
    'report_themes',
    'report_synthesis',
    'conversation',
    'custom_formats',
    'search_config',
  ];

  return (
    <div>
      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search prompts..."
          className="w-full pl-10 pr-4 py-2.5 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground mb-4">
        {filtered.length} prompt{filtered.length !== 1 ? "s" : ""}
        {search.trim() && ` matching "${search}"`}
      </p>

      {/* Grouped prompts */}
      <div className="space-y-8">
        {categoryOrder.map(category => {
          const prompts = grouped[category];
          if (!prompts || prompts.length === 0) return null;
          const label = PROMPT_CATEGORY_LABELS[category];

          return (
            <section key={category}>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-foreground">{label}</h3>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  {prompts.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {prompts.map(prompt => (
                  <button
                    key={prompt.id}
                    onClick={() => setSelectedPrompt(prompt)}
                    className="text-left p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                        {prompt.name}
                      </p>
                      <Sparkles className="w-3.5 h-3.5 text-amber-500/50 group-hover:text-amber-500 transition-colors flex-shrink-0 mt-0.5" />
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                      {prompt.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {prompt.usedIn.map(wfId => (
                        <span
                          key={wfId}
                          className="px-2 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary/70"
                        >
                          {WORKFLOWS[wfId]?.name || wfId}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No prompts matching "{search}"</p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedPrompt && (
        <PromptDetailModal
          prompt={selectedPrompt}
          onClose={() => setSelectedPrompt(null)}
        />
      )}
    </div>
  );
}
